"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Droplets, Wind, Layers, ThermometerSnowflake, ShieldCheck, Zap, ArrowRight, Factory, CircleCheck as CheckCircle2, SlidersHorizontal as Sliders, Sparkles } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Link } from "@/components/common/Link";
const systems = [{
  id: "atomizacao-ar",
  title: "Atomização a Ar e Água",
  subtitle: "Gotículas ultrafinas para evaporação rápida",
  desc: "Mistura ar comprimido e água para criar uma névoa fina homogénea, sem molhar superfícies ou equipamentos sensíveis.",
  tag: "Alta precisão",
  iconName: "Wind",
  points: ["Tamanho de gota inferior a 10 micras", "Controlo proporcional de débito", "Bicos autolimpantes para evitar entupimentos"],
  link: "/AirAtomizationNozzlesPage"
}, {
  id: "hidraulica",
  title: "Humidificação Hidráulica",
  subtitle: "Eficiência energética sem ar comprimido",
  desc: "Sistemas alimentados por pressão de água que garantem distribuição uniforme em grandes volumes de ar e condutas.",
  tag: "Baixo consumo",
  iconName: "Droplets",
  points: ["Operação direta em média e alta pressão", "Manutenção simplificada e baixo custo", "Amplo espectro de caudais disponíveis"],
  link: "/HydraulicAtomizationNozzlesPage"
}, {
  id: "automatizados",
  title: "Sistemas Automatizados AutoJet®",
  subtitle: "Regulação contínua e monitorização digital",
  desc: "Painéis de controlo dedicados com sensores de humidade relativa e temperatura que ajustam a pulverização em tempo real.",
  tag: "Indústria 4.0",
  iconName: "Sliders",
  points: ["Integração via Modbus / Profinet", "Prevenção ativa de desperdício hídrico", "Registo contínuo de parâmetros de processo"],
  link: "/AutomatedSprayingSystemsPage"
}];
const processBenefits = [{
  id: 1,
  title: "Controlo de eletricidade estática",
  desc: "Evita descargas indesejadas, paragens de linha e riscos de ignição em zonas de papel, cartão, filmes plásticos e têxteis."
}, {
  id: 2,
  title: "Preservação de matérias-primas",
  desc: "Mantém o teor de humidade de equilíbrio em madeira, tabaco, cereais e produtos frescos, evitando quebras e perda de peso."
}, {
  id: 3,
  title: "Supressão de poeiras em suspensão",
  desc: "Agrega micropartículas no ar fabril antes de assentarem no produto final ou nas vias respiratórias dos operadores."
}, {
  id: 4,
  title: "Qualidade de impressão e acabamento",
  desc: "Garante estabilidade dimensional das folhas e aderência consistente de tintas e revestimentos à base de água."
}];
const comfortBenefits = [{
  id: 1,
  title: "Arrefecimento adiabático natural",
  desc: "A evaporação da névoa reduz a temperatura do ar ambiente em até 8 °C sem recorrer a gases refrigerantes."
}, {
  id: 2,
  title: "Saúde e bem-estar ocupacional",
  desc: "Mantém a humidade relativa entre 40% e 60%, reduzindo a transmissão viral, irritações oculares e problemas respiratórios."
}, {
  id: 3,
  title: "Redução de custos de climatização",
  desc: "Alivia a carga térmica sobre os chillers e unidades de tratamento de ar (UTAs), reduzindo a fatura energética."
}, {
  id: 4,
  title: "Ambiente de trabalho controlado",
  desc: "Cria condições térmicas uniformes em pavilhões industriais abertos, armazéns logísticos e estufas agrícolas."
}];
const sectorTags = [{
  name: "Pasta e Papel",
  path: "/PulpPaperTissueCaseStudyPage"
}, {
  name: "Alimentar e Bebidas",
  path: "/FoodAndBeverageCaseStudyPage"
}, {
  name: "Madeira e Cimento",
  path: "/EngineeredWoodAndCementCaseStudyPage"
}, {
  name: "Data Centers",
  path: "/DataCentersCaseStudyPage"
}, {
  name: "Farmacêutica",
  path: "/PharmaceuticalCaseStudyPage"
}, {
  name: "Automóvel",
  path: "/AutomotiveManufacturingSuccessCasePage"
}];
export default function Humidification() {
  const shouldReduceMotion = useReducedMotion();
  const [activeTab, setActiveTab] = useState("processo");
  return <section id="humidificationsolutionsoverview" data-nav="light" className="bg-background text-foreground py-20 md:py-28 relative overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="max-w-3xl mb-16 md:mb-20">
          <div className="flex items-center gap-2 mb-4">
            <Badge variant="outline" className="border-secondary/30 text-secondary bg-secondary/10 px-3 py-1 font-medium">
              <Droplets className="size-3.5 mr-1" />
              Engenharia de Pulverização
            </Badge>
            <span className="text-xs uppercase tracking-wider text-muted-foreground">
              Tecnologia Spraying Systems Co.
            </span>
          </div>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-default">
            Soluções de humidificação industrial para processos e conforto
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
            Controlo rigoroso da humidade relativa para eliminar eletricidade estática, estabilizar matérias-primas e assegurar arrefecimento evaporativo com máxima eficiência hídrica.
          </p>
        </div>

        {/* 3 Core Systems Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-20">
          {systems.map((sys, idx) => <motion.div key={sys.id} data-index={idx} initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.45,
          delay: idx * 0.1
        }} className="h-full">
              <Card className="h-full border border-border/80 bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300 flex flex-col justify-between">
                <CardHeader>
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <div className="size-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                      {sys.iconName === "Wind" && <Wind className="size-5 text-primary" />}
                      {sys.iconName === "Droplets" && <Droplets className="size-5 text-primary" />}
                      {sys.iconName === "Sliders" && <Sliders className="size-5 text-primary" />}
                    </div>
                    <Badge variant="secondary" className="text-xs font-normal">
                      {sys.tag}
                    </Badge>
                  </div>
                  <CardTitle className="text-xl font-bold text-card-foreground">
                    {sys.title}
                  </CardTitle>
                  <CardDescription className="text-sm font-medium text-muted-foreground">
                    {sys.subtitle}
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-4">
                  <p className="text-sm text-foreground/80 leading-relaxed">
                    {sys.desc}
                  </p>

                  <div className="space-y-2 pt-2 border-t border-border/60">
                    {sys.points.map((pt, pIdx) => <div key={pIdx} data-index={pIdx} className="flex items-start gap-2 text-xs text-muted-foreground">
                        <CheckCircle2 className="size-4 text-secondary shrink-0 mt-0.5" />
                        <span>{pt}</span>
                      </div>)}
                  </div>
                </CardContent>

                <CardFooter className="pt-2">
                  <Button asChild variant="outline" size="sm" className="w-full justify-between">
                    <Link to={sys.link}>
                      Ver bicos e opções
                      <ArrowRight className="size-4 ml-1" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </div>

        {/* Benefits Tabs Section: Process vs Comfort */}
        <div className="bg-muted/40 border border-border rounded-2xl p-6 md:p-10 lg:p-12 mb-16">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                Impacto Operacional
              </span>
              <h3 className="text-2xl md:text-3xl font-bold text-foreground mt-1">
                Ganhos diretos no processo e no ambiente
              </h3>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full md:w-auto">
              <TabsList className="grid grid-cols-2 w-full md:w-80">
                <TabsTrigger value="processo" className="text-xs md:text-sm">
                  <Factory className="size-4 mr-1.5 hidden sm:inline-block" />
                  No Processo
                </TabsTrigger>
                <TabsTrigger value="conforto" className="text-xs md:text-sm">
                  <ThermometerSnowflake className="size-4 mr-1.5 hidden sm:inline-block" />
                  Conforto e Clima
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {activeTab === "processo" && <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {processBenefits.map((item, idx) => <div key={item.id} data-index={idx} className="bg-card border border-border/70 rounded-xl p-5 shadow-xs flex flex-col justify-start">
                  <div className="size-8 rounded-md bg-secondary/10 text-secondary flex items-center justify-center mb-3">
                    <Sparkles className="size-4" />
                  </div>
                  <h4 className="font-semibold text-foreground text-base mb-2">
                    {item.title}
                  </h4>
                  <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                    {item.desc}
                  </p>
                </div>)}
            </div>}

          {activeTab === "conforto" && <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {comfortBenefits.map((item, idx) => <div key={item.id} data-index={idx} className="bg-card border border-border/70 rounded-xl p-5 shadow-xs flex flex-col justify-start">
                  <div className="size-8 rounded-md bg-primary/10 text-primary flex items-center justify-center mb-3">
                    <ShieldCheck className="size-4" />
                  </div>
                  <h4 className="font-semibold text-foreground text-base mb-2">
                    {item.title}
                  </h4>
                  <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                    {item.desc}
                  </p>
                </div>)}
            </div>}
        </div>

        {/* Sectors and Technical Assistance CTA Banner */}
        <div className="bg-primary text-primary-foreground rounded-2xl p-8 md:p-12 relative overflow-hidden shadow-lg">
          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7 space-y-4">
              <Badge variant="outline" className="text-primary-foreground border-primary-foreground/30 bg-primary-foreground/10">
                Apoio de Engenharia
              </Badge>
              <h3 className="text-2xl md:text-3xl font-bold tracking-tight">
                Precisa de dimensionar a taxa de evaporação correta para a sua fábrica?
              </h3>
              <p className="text-sm md:text-base text-primary-foreground/85 max-w-xl leading-relaxed">
                Os nossos engenheiros analisam as dimensões do pavilhão, taxas de renovação de ar e necessidades higrométricas para calcular o número de bicos e a pressão ideal de funcionamento.
              </p>

              {/* Sectors chips */}
              <div className="pt-2">
                <p className="text-xs uppercase tracking-wider text-primary-foreground/70 mb-2 font-medium">
                  Setores com casos práticos:
                </p>
                <div className="flex flex-wrap gap-2">
                  {sectorTags.map((sector, sIdx) => <Link key={sector.name} to={sector.path} className="inline-flex text-xs px-2.5 py-1 rounded-md bg-primary-foreground/15 text-primary-foreground hover:bg-primary-foreground/25 transition-colors">
                      {sector.name}
                    </Link>)}
                </div>
              </div>
            </div>

            <div className="lg:col-span-5 flex flex-col sm:flex-row lg:flex-col gap-3 lg:items-end justify-center">
              <Button asChild size="lg" className="bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-md font-medium w-full sm:w-auto">
                <Link to="/ContactsPage">
                  Solicitar estudo técnico
                  <ArrowRight className="size-4 ml-1.5" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="bg-transparent border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 w-full sm:w-auto">
                <Link to="/TechnicalSolutionsPage">
                  Consultar soluções técnicas
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}